import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, matchPath } from 'react-router-dom';
import { useQuery } from 'data-fetching-client';
import { parseLocation } from 'automation-ui-routing/utils';
import GenerateActionsButton from 'automation-ui-canvas/components/panels/ai-assistant-panel/GenerateActionsButton';
import KeepSuggestedActionsButton from 'automation-ui-canvas/components/panels/ai-assistant-panel/KeepSuggestedActionsButton';
import { getActionsGenerationResponse, getActionsPrompt } from 'automation-ui-canvas/selectors/aiAssistantSelectors';
import { setAiActionsPrompt } from 'automation-ui-canvas/actions/AiAssistantActions';
import FeedbackFormForActions from 'automation-ui-enrollment/components/aiAssistant/FeedbackFormForActions';
import AiAssistantDisclaimer from 'automation-ui-enrollment/components/aiAssistant/AiAssistantDisclaimer';
import * as AbsolutePaths from 'automation-ui-canvas/constants/AbsolutePaths';
import { useGetFlowById } from 'automation-ui-canvas/queries/flowQueries';
import ContentAssistanceAlert from 'automation-ui-canvas/components/panels/ai-assistant-panel/ContentAssistanceAlert';
import AIFormControl from 'automation-ui-canvas/components/panels/ai-assistant-panel/AIFormControl';
import { GET_AI_SETTINGS_ACCESS } from 'automation-ui-canvas/queries/aiSettingsQueries';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { View } from 'foundations-components/components/View';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { WarningIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ActionsOptionPanelContent = ({
  flowId
}) => {
  var _aiSettingsData$aiSet;
  const initialPrompt = useSelector(getActionsPrompt);
  const [actionDescription, setActionDescription] = useState(() => initialPrompt);
  const [error, setError] = useState(false);
  const generationResponse = useSelector(getActionsGenerationResponse);
  const location = useLocation();
  const inReview = !!matchPath(location.pathname, AbsolutePaths.AI_ASSISTANT_GENERATE_ACTIONS);
  const edgeId = parseLocation(location).edgeId;
  const {
    data: flowByIdData
  } = useGetFlowById(flowId);
  const flow = flowByIdData === null || flowByIdData === void 0 ? void 0 : flowByIdData.flowById;
  const shouldAppendToEdge = edgeId && (flow === null || flow === void 0 ? void 0 : flow.isLeafNodeEdge(edgeId));
  const dispatch = useDispatch();

  // clear the initial prompt once it's been received
  useEffect(() => {
    if (initialPrompt.length > 0) {
      dispatch(setAiActionsPrompt(''));
    }
  }, [dispatch, initialPrompt]);
  const {
    data: aiSettingsData,
    loading: aiSettingsLoading
  } = useQuery(GET_AI_SETTINGS_ACCESS);
  const isContentAssistanceEnabled = (_aiSettingsData$aiSet = aiSettingsData === null || aiSettingsData === void 0 ? void 0 : aiSettingsData.aiSettingsAccess.crmPropertiesEnabled) !== null && _aiSettingsData$aiSet !== void 0 ? _aiSettingsData$aiSet : false;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!aiSettingsLoading && !isContentAssistanceEnabled && /*#__PURE__*/_jsx(ContentAssistanceAlert, {}), /*#__PURE__*/_jsx(AIFormControl, {
      label: /*#__PURE__*/_jsx(View, {
        marginInlineEnd: "300",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiAssistantPanel.body.addActionsPanel.actionDescriptionSection.title"
        })
      }),
      error: error,
      validationMessage: error ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "aiAssistantPanel.body.addActionsPanel.actionDescriptionSection.error"
      }) : null,
      children: /*#__PURE__*/_jsx(TextArea, {
        marginBlockEnd: "300",
        "data-test-id": "ai-assistant-action-description-input",
        readOnly: !isContentAssistanceEnabled,
        draggable: false,
        onChange: ({
          target: {
            value
          }
        }) => {
          setActionDescription(value);
        },
        placeholder: I18n.text('aiAssistantPanel.body.addActionsPanel.actionDescriptionSection.inputPlaceholder'),
        rows: 4,
        value: actionDescription
      })
    }), inReview ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "aiAssistantPanel.body.addActionsPanel.actionDescriptionSection.editActionsDetails"
      }), /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiAssistantPanel.body.addActionsPanel.actionDescriptionSection.regenerationDetails"
        })
      })]
    }) : !shouldAppendToEdge ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "aiAssistantPanel.body.addActionsPanel.actionDescriptionSection.generationDetails"
    }) : null, /*#__PURE__*/_jsx(View, {
      marginBlockStart: "300",
      marginBlockEnd: "200",
      children: inReview ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Alert, {
          type: "warning",
          use: "inline",
          marginBlockEnd: "400",
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            justifyContent: "center",
            children: [/*#__PURE__*/_jsx(Flex, {
              marginInlineEnd: "200",
              children: /*#__PURE__*/_jsx(WarningIcon, {
                color: "fill-caution-default"
              })
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "aiAssistantPanel.body.addActionsPanel.actionDescriptionSection.accuracyWarning"
            })]
          })
        }), /*#__PURE__*/_jsx(KeepSuggestedActionsButton, {
          aiPrompt: actionDescription,
          flowId: flowId
        }), /*#__PURE__*/_jsx(GenerateActionsButton, {
          edgeId: shouldAppendToEdge ? edgeId : undefined,
          flow: flow,
          flowId: flowId,
          onError: setError,
          userInput: actionDescription,
          isRegenerating: true
        })]
      }) : /*#__PURE__*/_jsx(GenerateActionsButton, {
        edgeId: shouldAppendToEdge ? edgeId : undefined,
        flow: flow,
        flowId: flowId,
        onError: setError,
        userInput: actionDescription,
        isRegenerating: false
      })
    }), isContentAssistanceEnabled && /*#__PURE__*/_jsx(AiAssistantDisclaimer, {}), inReview && generationResponse && /*#__PURE__*/_jsx(FeedbackFormForActions, {
      aiPrompt: actionDescription,
      generationResponse: generationResponse
    })]
  });
};
export default ActionsOptionPanelContent;